import { Input, Spin, notification, Modal } from 'antd';
import { SearchOutlined, CloseOutlined } from '@ant-design/icons';
import { useState, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { YgoproDeckCard } from 'src/model';
import debounce from 'lodash.debounce';

const StyledCardSearch = styled.div`
    position: relative;
    width: 100%;
    
    .trigger-button {
        width: 100%;
        padding: 10px 16px;
        border-radius: 6px;
        border: 2px solid var(--main-border, rgba(255, 255, 255, 0.1));
        background: var(--main-background-tertiary, rgba(13, 13, 13, 0.8));
        color: var(--main-text-primary, #fff);
        cursor: pointer;
        font-size: 14px;
        display: flex;
        align-items: center;
        gap: 8px;
        transition: all 0.3s ease;
        
        &:hover:not(:disabled) {
            border-color: var(--main-primary, rgba(24, 144, 255, 0.6));
            background: var(--main-primary, rgba(24, 144, 255, 0.1));
        }
        
        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
        
        .search-icon {
            font-size: 16px;
        }
        
        .placeholder-text {
            flex: 1;
            text-align: left;
            color: var(--color-contrast-60, rgba(255, 255, 255, 0.6));
        }
    }
`;

const StyledSearchModal = styled(Modal)`
    .ant-modal-content {
        background: var(--main-level-3, #1a1a1a);
        border-radius: 12px;
        overflow: hidden;
    }
    
    .ant-modal-header {
        background: var(--main-level-4, #0d0d0d);
        border-bottom: 1px solid var(--main-border, rgba(255, 255, 255, 0.1));
        padding: 16px 24px;
    }
    
    .ant-modal-title {
        color: var(--main-text-primary, #fff);
        font-size: 18px;
        font-weight: 600;
    }
    
    .ant-modal-body {
        padding: 0;
        max-height: 70vh;
        overflow-y: auto;
        
        &::-webkit-scrollbar {
            width: 8px;
        }
        
        &::-webkit-scrollbar-track {
            background: var(--main-level-4, #0d0d0d);
        }
        
        &::-webkit-scrollbar-thumb {
            background: var(--color-contrast-40, rgba(255, 255, 255, 0.4));
            border-radius: 4px;
            
            &:hover {
                background: var(--color-contrast-60, rgba(255, 255, 255, 0.6));
            }
        }
    }
    
    .ant-modal-footer {
        display: none;
    }
    
    .search-input-container {
        padding: 16px;
        background: var(--main-level-4, #0d0d0d);
        border-bottom: 1px solid var(--main-border, rgba(255, 255, 255, 0.1));
        position: sticky;
        top: 0;
        z-index: 10;
        
        .ant-input-affix-wrapper {
            background: var(--main-level-2, #262626);
            border: 2px solid var(--main-border, rgba(255, 255, 255, 0.1));
            border-radius: 8px;
            padding: 8px 12px;
            
            &:hover, &:focus, &.ant-input-affix-wrapper-focused {
                border-color: var(--main-primary, rgba(24, 144, 255, 0.6));
                box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
            }
            
            input {
                background: transparent;
                color: var(--main-text-primary, #fff);
                font-size: 16px;
                
                &::placeholder {
                    color: var(--color-contrast-60, rgba(255, 255, 255, 0.6));
                }
            }
            
            .ant-input-prefix, .ant-input-suffix {
                color: var(--color-contrast-60, rgba(255, 255, 255, 0.6));
            }
        }
    }
`;

const StyledSearchResults = styled.div`
    padding: 16px;
    
    .search-status {
        text-align: center;
        padding: 40px 20px;
        color: var(--color-contrast-60, rgba(255, 255, 255, 0.6));
        font-size: 14px;
    }
    
    .results-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
        gap: 16px;
        padding: 8px;
    }
    
    .card-item {
        position: relative;
        cursor: pointer;
        border-radius: 8px;
        overflow: hidden;
        background: var(--main-level-4, #0d0d0d);
        border: 2px solid transparent;
        transition: all 0.3s ease;
        
        &:hover {
            transform: translateY(-4px);
            border-color: var(--main-primary, rgba(24, 144, 255, 0.6));
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
            
            .card-overlay {
                opacity: 1;
            }
        }
        
        .card-image-wrapper {
            position: relative;
            width: 100%;
            padding-bottom: 145%; /* Yu-Gi-Oh card aspect ratio */
            background: var(--main-level-2, #262626);
            overflow: hidden;
            
            img {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                object-fit: cover;
            }
        }
        
        .card-overlay {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            background: linear-gradient(to top, rgba(0, 0, 0, 0.95) 0%, rgba(0, 0, 0, 0.7) 70%, transparent 100%);
            padding: 12px 8px 8px;
            opacity: 0;
            transition: opacity 0.3s ease;
            
            .card-name {
                font-weight: 600;
                font-size: 12px;
                color: #fff;
                margin-bottom: 4px;
                line-height: 1.3;
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            
            .card-type {
                font-size: 10px;
                color: var(--color-contrast-80, rgba(255, 255, 255, 0.8));
                display: flex;
                align-items: center;
                gap: 4px;
                
                .type-badge {
                    display: inline-block;
                    padding: 2px 6px;
                    background: var(--main-primary, rgba(24, 144, 255, 0.3));
                    border-radius: 4px;
                    font-size: 9px;
                    font-weight: 600;
                    text-transform: uppercase;
                }
            }
        }
        
        .card-info-compact {
            padding: 8px;
            
            .card-name-compact {
                font-weight: 500;
                font-size: 11px;
                color: var(--main-text-primary, #fff);
                line-height: 1.3;
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                overflow: hidden;
                text-overflow: ellipsis;
                min-height: 28px;
            }
        }
    }
    
    @media (max-width: 768px) {
        .results-grid {
            grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
            gap: 12px;
        }
    }
`;

export type CardSearchProps = {
    onSelect: (card: YgoproDeckCard) => void,
    placeholder?: string,
    disabled?: boolean,
};

export const CardSearch = ({ onSelect, placeholder, disabled }: CardSearchProps) => {
    const [searchResults, setSearchResults] = useState<YgoproDeckCard[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [modalVisible, setModalVisible] = useState(false);
    const abortControllerRef = useRef<AbortController | null>(null);
    const inputRef = useRef<any>(null);

    const searchCards = useCallback(async (query: string) => {
        if (!query || query.trim().length < 2) {
            setSearchResults([]);
            return;
        }

        // Cancel previous request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        abortControllerRef.current = new AbortController();
        setLoading(true);

        try {
            const response = await fetch(
                `https://db.ygoprodeck.com/api/v7/cardinfo.php?fname=${encodeURIComponent(query)}&num=20&offset=0&misc=yes`,
                { signal: abortControllerRef.current.signal }
            );

            if (!response.ok) {
                throw new Error(`Response status: ${response.status}`);
            }

            const data: { data: YgoproDeckCard[] } = await response.json();
            
            // Sort by popularity
            const sortedCards = data.data.sort((l, r) => {
                const lMisc = l.misc_info?.[0];
                const rMisc = r.misc_info?.[0];

                if (!lMisc || !rMisc) return 0;

                const viewWeekRatio = (lMisc.viewsweek + 1) / (rMisc.viewsweek + 1);
                if (viewWeekRatio > 2) return rMisc.viewsweek - lMisc.viewsweek;

                return rMisc.views - lMisc.views;
            });

            setSearchResults(sortedCards);
        } catch (error: any) {
            if (error.name !== 'AbortError') {
                console.error('Search error:', error);
                notification.error({
                    message: 'Search failed',
                    description: 'Could not search for cards. Please try again.',
                });
                setSearchResults([]);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    const debouncedSearch = useCallback(
        (val: string) => {
            const debouncedFn = debounce(() => searchCards(val), 400);
            debouncedFn();
        },
        [searchCards]
    );

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchValue(value);
        debouncedSearch(value);
    };

    const handleCardSelect = (card: YgoproDeckCard) => {
        onSelect(card);
        setSearchValue('');
        setSearchResults([]);
        setModalVisible(false);
    };

    const handleModalClose = () => {
        setModalVisible(false);
        setSearchValue('');
        setSearchResults([]);
    };

    const handleOpenModal = () => {
        if (!disabled) {
            setModalVisible(true);
            // Focus input after modal opens
            setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
        }
    };

    const getCardTypeDisplay = (card: YgoproDeckCard) => {
        if (card.frameType === 'spell' || card.frameType === 'trap') {
            return (
                <>
                    <span className="type-badge">{card.frameType}</span>
                    {card.race && <span>{card.race}</span>}
                </>
            );
        }
        return (
            <>
                <span className="type-badge">{card.attribute}</span>
                <span>{card.race}</span>
                {card.level && <span>Lv.{card.level}</span>}
                {card.linkval && <span>Link-{card.linkval}</span>}
            </>
        );
    };

    return (
        <StyledCardSearch>
            <button 
                className="trigger-button"
                onClick={handleOpenModal}
                disabled={disabled}
            >
                <SearchOutlined className="search-icon" />
                <span className="placeholder-text">
                    {placeholder || 'Search for a card...'}
                </span>
            </button>
            
            <StyledSearchModal
                visible={modalVisible}
                onCancel={handleModalClose}
                width={900}
                title={
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span>Search Yu-Gi-Oh Cards</span>
                        <CloseOutlined 
                            onClick={handleModalClose}
                            style={{ cursor: 'pointer', fontSize: '16px' }}
                        />
                    </div>
                }
                closeIcon={null}
            >
                <div className="search-input-container">
                    <Input
                        ref={inputRef}
                        size="large"
                        placeholder="Type card name to search..."
                        prefix={<SearchOutlined />}
                        suffix={loading ? <Spin size="small" /> : null}
                        value={searchValue}
                        onChange={handleSearch}
                        autoFocus
                    />
                </div>
                
                <StyledSearchResults>
                    {loading && (
                        <div className="search-status">
                            <Spin size="large" />
                            <div style={{ marginTop: '16px' }}>Searching for cards...</div>
                        </div>
                    )}
                    
                    {!loading && searchResults.length === 0 && searchValue.length >= 2 && (
                        <div className="search-status">
                            No cards found for "{searchValue}"
                        </div>
                    )}
                    
                    {!loading && searchResults.length === 0 && searchValue.length < 2 && (
                        <div className="search-status">
                            Type at least 2 characters to search
                        </div>
                    )}
                    
                    {!loading && searchResults.length > 0 && (
                        <div className="results-grid">
                            {searchResults.map(card => (
                                <div 
                                    key={card.id}
                                    className="card-item"
                                    onClick={() => handleCardSelect(card)}
                                >
                                    <div className="card-image-wrapper">
                                        {card.card_images?.[0]?.image_url_small && (
                                            <img 
                                                src={card.card_images[0].image_url_small} 
                                                alt={card.name}
                                                loading="lazy"
                                            />
                                        )}
                                    </div>
                                    <div className="card-overlay">
                                        <div className="card-name">{card.name}</div>
                                        <div className="card-type">
                                            {getCardTypeDisplay(card)}
                                        </div>
                                    </div>
                                    <div className="card-info-compact">
                                        <div className="card-name-compact">{card.name}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </StyledSearchResults>
            </StyledSearchModal>
        </StyledCardSearch>
    );
};
